import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { DocumentListResult } from '@newStore/documentList/newDocumentListTypes';
import {
  selectAreAllResultsSelected,
  selectColumnsToShow,
  selectSearchResults,
} from '@newStore/documentList/newDocumentListSelectors';
import {
  loadMore,
  refreshList,
  sortByColumn,
  toggleSelectAll,
  toggleSelected,
} from '@newStore/documentList/newDocumentListState';
import { attachmentDocumentTypes } from '@newStore/documentUI/transformContent/attachmentHelpers';
import FiltersBar from "../filtersBar/FiltersBar";
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import './documentListTable.scss';
import DocumentListHeader from '../documentListHeader/DocumentListHeader';
import { useTypedSelector } from '../../../hooks/useTypedSelector';

const DocumentListTable: React.FC = () => {
  const dispatch = useDispatch();
  
  // Get data from Redux state using the same selectors as the AngularJS component
  const results = useTypedSelector(selectSearchResults);
  const loading = useTypedSelector((state) => state.newDocumentList.loading);
  const loadingMore = useTypedSelector((state) => state.newDocumentList.loadingMore);
  const nextLink = useTypedSelector((state) => state.newDocumentList.nextLink);
  const allSelected = useTypedSelector(selectAreAllResultsSelected);
  const selectedState = useTypedSelector((state) => state.newDocumentList.selected);
  const columnsToShow = useTypedSelector(selectColumnsToShow);

  useEffect(() => {
    dispatch(refreshList());
  }, [dispatch]);

  const isResultSelected = (result: DocumentListResult): boolean => {
    const isSelected = selectedState[result.$$meta.permalink] === true;
    return isSelected;
  };

  const handleSelectAll = () => {
    dispatch(toggleSelectAll());
  };

  const handleToggleSelected = (result: DocumentListResult, event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    dispatch(toggleSelected({ href: result.$$meta.permalink }));
  };

  const handleSortByColumn = (column: string) => {
    dispatch(sortByColumn({ column }));
  };

  const handleRowClick = (result: DocumentListResult) => {
     getAngularService('$state').go('edit', { key: result.key });
  };

  const handleLoadMore = () => {
    if (nextLink) {
      dispatch(loadMore({ href: nextLink }));
    }
  };

  const getContentType = (result: DocumentListResult): string => {
    if (result?.attachments) {
      const attachment = result.attachments.find((a) => a.type === 'CONTENT');
      return attachment ? attachment.contentType : '';
    }
    return '';
  };

  const getDocumentType = (result: DocumentListResult): string => {
    if (result?.tags) {
      return result.tags
        .filter((t) => attachmentDocumentTypes.some((type) => type.value === t))
        .map((t) => {
          return attachmentDocumentTypes.find((type) => type.value === t)?.display || '';
        })
        .join(',');
    }
    return '';
  };

  return (
    <div className="documents">
      <DocumentListHeader />
      <div className="layout-r">
        <div className="row">
          <FiltersBar />
          <div className="col-lg-9 col-md-9 has-scrollbar results-panel">
            <div className="is-scrollable-sm">
              {loading && (
                <div>
                  <div className="spinner-icon bigCenter" style={{ marginBottom: '30px' }} />
                </div>
              )}

              {!loading && (
                <div className="table-responsive">
                  <table className="table-search table table-align-middle table-hover">
                    <thead>
                      <tr>
                        <th className="table-checkbox-column">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th className="table-thumbnail-column-sm">
                          {/* Thumbnail */}
                        </th>
                        <th onClick={() => handleSortByColumn('title')} style={{ cursor: 'pointer' }}>
                          Titel
                        </th>
                        {columnsToShow.hasAllGlobalDocumentResults && <th>Referentienummer</th>}
                        {columnsToShow.hasAllGlobalDocumentResults && <th>Documenttype</th>}
                        {columnsToShow.hasAllGlobalDocumentResults && <th>Contenttype</th>}
                        {columnsToShow.hasSomeProThemaResult && <th>Auteurs</th>}
                        <th 
                          onClick={() => handleSortByColumn('$$meta.modified')} 
                          className="text-muted"
                          style={{ cursor: 'pointer' }}
                        >
                          Gewijzigd
                        </th>
                        {columnsToShow.souldShowProposalsLabel && <th />}
                        {columnsToShow.hasSomeProThemaResult && <th />}
                        <th>Publicatiedatum</th>
                        <th onClick={() => handleSortByColumn('type,tags')} style={{ cursor: 'pointer' }}>
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results?.map((result) => (
                        <tr
                          key={result.key}
                          className={isResultSelected(result) ? 'selected' : ''}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="table-checkbox-column" onClick={(e) => handleToggleSelected(result, e)}>
                            <div className="holder">
                              <input
                                type="checkbox"
                                checked={isResultSelected(result)}
                                readOnly
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                          </td>
                          <td 
                            className="table-thumbnail-column-sm"
                            onClick={() => handleRowClick(result)}
                          >
                            <div className="holder">
                              <img src={result.$$iconSrc} width="30" alt="" />
                            </div>
                          </td>
                          <td
                            onClick={() => handleRowClick(result)}
                            style={{
                              maxWidth: '400px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                            title={result.$$titleAsHtml}
                          >
                            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from backend */}
                            <span dangerouslySetInnerHTML={{ __html: result.$$titleAsHtml }} />
                          </td>
                          {columnsToShow.hasAllGlobalDocumentResults && (
                            <td onClick={() => handleRowClick(result)}>
                              <span>{result.identifiers?.join(',') || ''}</span>
                            </td>
                          )}
                          {columnsToShow.hasAllGlobalDocumentResults && (
                            <td onClick={() => handleRowClick(result)}>
                              <span>{getDocumentType(result)}</span>
                            </td>
                          )}
                          {columnsToShow.hasAllGlobalDocumentResults && (
                            <td onClick={() => handleRowClick(result)}>
                              <span>{getContentType(result)}</span>
                            </td>
                          )}
                          {columnsToShow.hasSomeProThemaResult && (
                            <td onClick={() => handleRowClick(result)}>
                              <span>{result.expandedAuthors}</span>
                            </td>
                          )}
                          <td onClick={() => handleRowClick(result)}>
                            <span className="text-muted">{result.$$meta.modified}</span>
                          </td>
                          {columnsToShow.souldShowProposalsLabel && (
                            <td onClick={() => handleRowClick(result)}>
                              {result.hasProposalsToReview && (
                                <span className="label label-review">EINDREDACTIE</span>
                              )}
                            </td>
                          )}
                          {columnsToShow.hasSomeProThemaResult && (
                            <td onClick={() => handleRowClick(result)}>
                              {result.issued ? (
                                <span className="label label-published">GEPUBLICEERD</span>
                              ) : (
                                <span className="label label-default">CONCEPT</span>
                              )}
                            </td>
                          )}
                          <td className="text-nowrap" onClick={() => handleRowClick(result)}>
                            {result.issued && <span>{result.issued}</span>}
                          </td>
                          <td className="text-nowrap content-type-label" onClick={() => handleRowClick(result)}>
                            <div className="holder">
                              <span>
                                {result.$$typeConfig.information?.single || result.$$uiType}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ marginBottom: '30px' }}>
                    {!loadingMore && nextLink && (
                      <div onClick={handleLoadMore} style={{ textAlign: 'center' }}>
                        <div className="btn btn-default">Laad meer</div>
                      </div>
                    )}
                    {loadingMore && (
                      <div>
                        <div className="spinner-icon mediumCenter" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentListTable;